import { AskAiPanel } from '../modules/vendor/assistant/components/AskAiPanel';
import { PricingPage } from '../pages/superadmin/pricing/pricing.page';
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Navigate, Route, Routes } from "react-router-dom";
import { cn } from "@/lib/utils";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { SHELL_HEIGHT } from '../modules/vendor/assistant/askai-layout';
import { SideBar } from "../common/Header/SideBar";
import { VendorState } from "../redux/vendor/rootReducer";
import { AdminOverviewPage } from "../pages/superadmin/overview/admin-overview.page";
import { AdminNotFoundPage } from "../pages/superadmin/not-found/admin-not-found.page";
import { AdminNavSync } from "../modules/superadmin/shell/admin-nav.store";
import OurVendors from "../pages/superadmin/vendors/our-vendors.page";
import { EditVendor } from "../pages/superadmin/vendors/edit-vendor.page";
import { Users } from "../pages/superadmin/users/users.page";
import { EditUser } from "../pages/superadmin/users/edit-user.page";
import { InviteUser } from "../pages/superadmin/users/invite-user.page";
import { InvitationsPage } from "../pages/superadmin/users/invitations.page";
import OurSpaces from "../pages/superadmin/spaces/our-spaces.page";
import { QueueMonitorPage } from "../pages/superadmin/queue-monitor/queue-monitor.page";
import { PlatformCostsPage } from "../pages/superadmin/costs/platform-costs.page";
import { ProductFamilies } from "../pages/superadmin/product-families/product-families.page";
import { EditProductFamily } from "../pages/superadmin/product-families/edit-product-family.page";
import { ProductTypes } from "../pages/superadmin/product-types/product-types.page";
import { EditProductType } from "../pages/superadmin/product-types/edit-product-type.page";
import { SpaceManagerPage } from "../pages/superadmin/space-manager/space-manager.page";
// The Ardealy Network: the surface graph, matching and its curation queue.
import { NetworkOverviewPage } from "../pages/superadmin/network/network-overview.page";
import { NetworkBrandsPage } from "../pages/superadmin/network/network-brands.page";
import { NetworkBrandPage } from "../pages/superadmin/network/network-brand.page";
import { NetworkProductPage } from "../pages/superadmin/network/network-product.page";
import { NetworkReviewPage } from "../pages/superadmin/network/network-review.page";
import { PlansPage } from "../pages/superadmin/plans/plans.page";
import { EditPlanPage } from "../pages/superadmin/plans/edit-plan.page";
import { WireInvoicesPage } from "../pages/superadmin/billing/wire-invoices.page";
import { WireSettingsPage } from "../pages/superadmin/billing/wire-settings.page";
import { QuoteRequestsPage } from "../pages/superadmin/sales/quote-requests.page";
import { CrmPage } from "../pages/superadmin/crm/crm.page";
import { SignupsPage } from "../pages/superadmin/signups/signups.page";
import { VendorUsagePage } from "../pages/superadmin/vendor-usage/vendor-usage.page";
// feat/room-to-revenue — docs/room-to-revenue.md
import { UnitEconomicsPage } from "../pages/superadmin/unit-economics/unit-economics.page";
import { DemoSitesPage } from "../pages/superadmin/demo-sites/demo-sites.page";
import { DemoGeneratorPage } from "../pages/superadmin/demo-generator/demo-generator.page";
import { ModelGeneratorPage } from "../pages/superadmin/model-generator/model-generator.page";
import { SupportInboxPage } from "../pages/superadmin/support/support-inbox.page";
import { SupportBoardPage } from "../pages/superadmin/support/support-board.page";
import { TeamDevicesPage } from "../pages/superadmin/team-devices/team-devices.page";
import NotificationsPage from "../pages/common/notifications/notifications.page";
import { SupportSessionStartPage } from "../pages/superadmin/support-session/support-session-start.page";
import { StaffSupportSync } from "../modules/support/staff/staff-support.store";
import { PreviewTagSync } from "../common/services/preview-tag.service";

export function SuperAdminPrivateRoutes() {
    const { isDrawerOpen } = useSelector((state: VendorState) => state.ui);
    const { i18n } = useTranslation();

    return (
        <div dir={i18n.language === "ar" ? "rtl": "ltr"}>
                    <SidebarProvider className="h-screen min-h-0 w-full">
                        <SideBar/>
                        <SidebarInset
                            /* The docked assistant is a sticky column in here and has to be
                               exactly this tall — a sticky box taller than what it scrolls in
                               cannot stick. Shared so the two cannot drift apart. */
                            style={{ height: SHELL_HEIGHT }}
                            className={cn('flex-row items-start overflow-auto', isDrawerOpen && 'invisible')}
                        >
                          {/*
                            * The assistant lives INSIDE the scrolling column, as a
                            * sticky sibling of the page rather than a layer over it.
                            *
                            * That is what keeps the scrollbar at the window's edge. A
                            * fixed panel meant this column had to be narrowed to make
                            * room, which moved its scrollbar 460px inland — a scrollbar
                            * floating in the middle of the screen. As a column in here,
                            * the scroller still reaches the right edge and the panel
                            * simply sits beside it.
                            */}
                          {/* Full width on purpose. The reading measure is applied to page
                                content inside PageContainer, so the top bar can span the
                                window while the page below it stays centred. */}
                            {/* minWidth:0 so a wide table shrinks with the column
                                instead of pushing the assistant off the edge. */}
                            <div className="min-w-0 flex-grow">
                            <Routes>
                              <Route path="dashboard" element={<AdminOverviewPage />} />
                              {/* The first page of a support session frame ("Act-as"). */}
                              <Route path="support/:sessionId" element={<SupportSessionStartPage />} />

                              <Route path='vendors'>
                                  <Route path='' element={<OurVendors />} />
                                  <Route path='new' element={<EditVendor />} />
                                  <Route path=':vendorId/edit' element={<EditVendor />} />
                              </Route>

                              <Route path="signups" element={<SignupsPage />} />

                              <Route path="vendor-usage" element={<VendorUsagePage />} />

                              <Route path='users'>
                                  <Route path='' element={<Users />} />
                                  <Route path='new' element={<InviteUser />} />
                                  <Route path='invitations' element={<InvitationsPage />} />
                                  <Route path=':userId/edit' element={<EditUser />} />
                              </Route>

                              <Route path='spaces'>
                                  <Route path='' element={<OurSpaces />} />
                              </Route>

                              <Route path="queue-monitor" element={<QueueMonitorPage />} />
                              <Route path="costs" element={<PlatformCostsPage />} />
                              {/* Revenue, cost and margin per customer, and the feature review (feat/room-to-revenue). */}
                              <Route path="unit-economics" element={<UnitEconomicsPage />} />

                              <Route path='product-families'>
                                  <Route path='' element={<ProductFamilies />} />
                                  <Route path='new' element={<EditProductFamily />} />
                                  <Route path=':familyId/edit' element={<EditProductFamily />} />
                              </Route>

                              <Route path='product-types'>
                                  <Route path='' element={<ProductTypes />} />
                                  <Route path='new' element={<EditProductType />} />
                                  <Route path=':productTypeId/edit' element={<EditProductType />} />
                              </Route>

                              <Route path="space-manager" element={<SpaceManagerPage />} />

                              <Route path="network">
                                  <Route path="" element={<NetworkOverviewPage />} />
                                  <Route path="brands" element={<NetworkBrandsPage />} />
                                  <Route path="brands/:id" element={<NetworkBrandPage />} />
                                  <Route path="products/:id" element={<NetworkProductPage />} />
                                  <Route path="review" element={<NetworkReviewPage />} />
                              </Route>

                              <Route path='plans'>
                                  <Route path='' element={<PlansPage />} />
                                  <Route path='new' element={<EditPlanPage />} />
                                  <Route path=':planId/edit' element={<EditPlanPage />} />
                              </Route>

                              <Route path="pricing" element={<PricingPage />} />
                              <Route path="wire-invoices" element={<WireInvoicesPage />} />
                              <Route path="wire-settings" element={<WireSettingsPage />} />
                              <Route path="quote-requests" element={<QuoteRequestsPage />} />
                              {/* Allowlisted admins only (CRM_ALLOWED_EMAILS); the menu entry is shown to them alone. */}
                              <Route path="crm" element={<CrmPage />} />
                              <Route path="demo-sites" element={<DemoSitesPage />} />
                              <Route path="demo-generator" element={<DemoGeneratorPage />} />
                              <Route path="tools/3d-generator" element={<ModelGeneratorPage />} />
                              <Route path="rug-generator" element={<Navigate to="/tools/3d-generator" replace />} />

                              <Route path="support">
                                  <Route path="" element={<SupportInboxPage />} />
                                  <Route path="board" element={<SupportBoardPage />} />
                              </Route>

                              <Route path="team-devices" element={<TeamDevicesPage />} />
                              <Route path="notifications" element={<NotificationsPage portal="staff" />} />

                              <Route path="*" element={<AdminNotFoundPage />} />

                            </Routes>
                          </div>
                            <AskAiPanel />
                            {/* Keeps the support inbox badge current. */}
                            <StaffSupportSync />
                            <PreviewTagSync />
                            {/* Keeps the sidebar badges (quotes, invoices, stuck jobs) current. */}
                            <AdminNavSync />
                        </SidebarInset>
                    </SidebarProvider>
        </div>
    );
}
